<template>
  <BaseCard
    class="dark:bg-muted-900 pointer-events-auto relative z-[200] flex border bg-white shadow-md transition-all duration-200 hover:shadow-lg"
    :class="wrapperClasses"
    :role="ariaRole"
    :aria-live="ariaLive"
    :aria-atomic="true"
    :aria-label="accessibleLabel"
    tabindex="-1"
    @mouseenter="$emit('pause')"
    @mouseleave="$emit('resume')"
    @focusin="$emit('pause')"
    @focusout="$emit('resume')"
  >
    <div class="absolute inset-x-0 bottom-0 h-1">
      <div
        class="h-1 rounded-es-lg transition-all duration-75 ease-linear"
        :class="progressClasses"
        :style="{
          width: `${toast.progress * 100}%`,
        }"
      />
    </div>

    <div v-if="toast.closable" class="absolute end-2 top-2">
      <BaseButtonClose
        :aria-label="`Close ${accessibleLabel}`"
        @click="$emit('close')"
      />
    </div>

    <div
      v-if="toast.icon"
      class="flex w-16 items-center justify-center rounded-s-lg"
      :class="iconBgClasses"
    >
      <Icon
        :name="toast.icon"
        class="size-6"
        :class="iconClasses"
      />
    </div>
    <div class="text-md min-w-[220px] max-w-xl py-4 pe-12 ps-8">
      <h3
        v-if="toast.title"
        class="text-theme-content dark:text-theme-content-inverted text-semibold font-heading maw-w-lg block truncate font-semibold"
      >
        {{ toast.title }}
      </h3>
      <p
        v-if="toast.message"
        class="text-muted-600 dark:text-muted-400 whitespace-pre-wrap"
      >
        {{ toast.message }}
      </p>
    </div>
  </BaseCard>
</template>

<script setup lang="ts">
import type { ToastItem } from '~/stores/toaster'

interface Props {
  toast: ToastItem
}

interface Emits {
  (e: 'close'): void
  (e: 'pause'): void
  (e: 'resume'): void
}

const props = defineProps<Props>()
defineEmits<Emits>()

// WCAG 4.1.3: Determine appropriate ARIA live region politeness based on toast type
const ariaLive = computed(() => {
  // Danger/error messages should be assertive to interrupt the user
  if (props.toast.color === 'danger') {
    return 'assertive'
  }
  // All other status messages should be polite to not interrupt
  return 'polite'
})

// WCAG 4.1.3: Determine appropriate role based on toast type
const ariaRole = computed(() => {
  // Error messages should use alert role for immediate attention
  if (props.toast.color === 'danger') {
    return 'alert'
  }
  // All other messages use status role for status updates
  return 'status'
})

// WCAG 4.1.3: Create accessible label for screen readers
const accessibleLabel = computed(() => {
  const parts = []

  // Add color context for screen readers
  const colorContext = {
    success: 'Success',
    danger: 'Error',
    warning: 'Warning',
    info: 'Information',
    primary: 'Notification',
    muted: 'Notification',
  }

  parts.push(colorContext[props.toast.color || 'muted'] || 'Notification')

  if (props.toast.title) {
    parts.push(props.toast.title)
  }

  if (props.toast.message) {
    parts.push(props.toast.message)
  }

  return parts.join(': ')
})

const wrapperClasses = computed(() => {
  switch (props.toast.color) {
    case 'primary':
      return 'dark:border-primary-800 border-primary-200'
    case 'success':
      return 'dark:border-success-800 border-success-200'
    case 'danger':
      return 'dark:border-danger-800 border-danger-200'
    case 'warning':
      return 'dark:border-warning-800 border-warning-200'
    case 'info':
      return 'dark:border-info-800 border-info-200'
    case 'muted':
    default:
      return 'dark:border-muted-800 border-muted-200'
  }
})

const progressClasses = computed(() => {
  switch (props.toast.color) {
    case 'primary':
      return 'bg-primary-200 dark:bg-primary-900'
    case 'success':
      return 'bg-success-200 dark:bg-success-900'
    case 'danger':
      return 'bg-danger-200 dark:bg-danger-900'
    case 'warning':
      return 'bg-warning-200 dark:bg-warning-900'
    case 'info':
      return 'bg-info-200 dark:bg-info-900'
    case 'muted':
    default:
      return 'bg-muted-200 dark:bg-muted-900'
  }
})

const iconBgClasses = computed(() => {
  switch (props.toast.color) {
    case 'primary':
      return 'bg-primary-50 dark:bg-primary-700'
    case 'success':
      return 'bg-success-50 dark:bg-success-700'
    case 'danger':
      return 'bg-danger-50 dark:bg-danger-700'
    case 'warning':
      return 'bg-warning-50 dark:bg-warning-700'
    case 'info':
      return 'bg-info-50 dark:bg-info-700'
    case 'muted':
    default:
      return 'bg-muted-50 dark:bg-muted-700'
  }
})

const iconClasses = computed(() => {
  switch (props.toast.color) {
    case 'primary':
      return 'text-primary-400 dark:text-primary-50'
    case 'success':
      return 'text-success-400 dark:text-success-50'
    case 'danger':
      return 'text-danger-400 dark:text-danger-50'
    case 'warning':
      return 'text-warning-400 dark:text-warning-50'
    case 'info':
      return 'text-info-400 dark:text-info-50'
    case 'muted':
    default:
      return 'text-muted-400 dark:text-muted-50'
  }
})
</script>
